import { z } from 'zod';
import { appointmentStatusSchema, type AppointmentStatus } from './enums.js';

/** Appointment contracts (docs/spec.md §4.2, §7 screen 3). */

export const appointmentInputSchema = z.object({
  patientId: z.coerce.number().int().positive(),
  doctorId: z.coerce.number().int().positive().optional(),
  /** Local wall-clock time, "YYYY-MM-DDTHH:mm" — the clinic works in one zone. */
  scheduledAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'datetime_format'),
  durationMin: z.coerce.number().int().min(5).max(480).default(30),
  type: z.string().trim().max(120).optional().or(z.literal('')),
  note: z.string().trim().max(2000).optional().or(z.literal('')),
  /**
   * Books anyway when the doctor already has an overlapping appointment.
   * Clinics do double-book deliberately, so the clash is surfaced as a question
   * rather than a wall — but it must be an explicit choice, not a silent one.
   */
  force: z.boolean().optional(),
});
export type AppointmentInput = z.infer<typeof appointmentInputSchema>;

export const appointmentUpdateSchema = appointmentInputSchema.partial().extend({
  status: appointmentStatusSchema.optional(),
});
export type AppointmentUpdate = z.infer<typeof appointmentUpdateSchema>;

export interface Appointment {
  id: number;
  patientId: number;
  patientName: string;
  patientCode: string;
  doctorId: number | null;
  doctorName: string | null;
  scheduledAt: string;
  durationMin: number;
  type: string | null;
  status: AppointmentStatus;
  note: string | null;
}

/** One entry per day that has appointments, for the month grid. */
export interface CalendarDay {
  date: string;
  count: number;
}

/**
 * Which statuses may follow the current one.
 *
 * Encoded once and shared so the UI offers the same moves the API accepts.
 * `done` and `no_show` are terminal — reopening a closed visit should mean
 * creating a new appointment, not editing history.
 */
export const APPOINTMENT_TRANSITIONS: Record<AppointmentStatus, readonly AppointmentStatus[]> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['arrived', 'cancelled', 'no_show'],
  arrived: ['done', 'cancelled'],
  done: [],
  cancelled: [],
  no_show: [],
};

export function canTransition(from: AppointmentStatus, to: AppointmentStatus): boolean {
  return APPOINTMENT_TRANSITIONS[from].includes(to);
}
